import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import autoTable from 'jspdf-autotable';
import { createPDF, autoTableDefaults } from '../pdfUtils';

/* ─────────────────────── SVG CHART COMPONENTS ─────────────────────── */

const PALETTE = ['#1a237e','#283593','#3949ab','#5c6bc0','#7986cb',
                 '#e53935','#fb8c00','#43a047','#00897b','#8e24aa',
                 '#00acc1','#f4511e','#6d4c41','#546e7a','#e91e63'];

function DonutChart({ data = [], size = 200, innerR }) {
  const r = (size / 2) - 10;
  const iR = innerR ?? r * 0.55;
  const center = size / 2;
  const total = data.reduce((s, d) => s + Number(d.value), 0);
  const [hov, setHov] = useState(null);
  if (!total) return (
    <svg width={size} height={size}>
      <text x={center} y={center} textAnchor="middle" fontSize="12" fill="#999">Veri yok</text>
    </svg>
  );
  let start = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const pct = Number(d.value) / total;
    const end = start + pct * 2 * Math.PI;
    const x1 = center + r * Math.cos(start), y1 = center + r * Math.sin(start);
    const x2 = center + r * Math.cos(end),   y2 = center + r * Math.sin(end);
    const ix1 = center + iR * Math.cos(start), iy1 = center + iR * Math.sin(start);
    const ix2 = center + iR * Math.cos(end),   iy2 = center + iR * Math.sin(end);
    const large = pct > 0.5 ? 1 : 0;
    const path = `M${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} L${ix2},${iy2} A${iR},${iR} 0 ${large},0 ${ix1},${iy1} Z`;
    const slice = { path, color: PALETTE[i % PALETTE.length], label: d.label, value: d.value, pct };
    start = end;
    return slice;
  });
  return (
    <svg width={size} height={size} style={{ overflow: 'visible' }}>
      {slices.map((s, i) => (
        <path key={i} d={s.path} fill={s.color} opacity={hov === i ? 0.8 : 1}
          onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}
          style={{ cursor: 'pointer', transition: 'opacity .15s' }}>
          <title>{s.label}: {s.value} (%{(s.pct * 100).toFixed(1)})</title>
        </path>
      ))}
      {hov !== null ? (
        <>
          <text x={center} y={center - 8} textAnchor="middle" fontSize="11" fill="#333" fontWeight="bold">
            {slices[hov].label.length > 12 ? slices[hov].label.slice(0, 12) + '…' : slices[hov].label}
          </text>
          <text x={center} y={center + 10} textAnchor="middle" fontSize="13" fill="#1a237e" fontWeight="bold">
            {Number(slices[hov].value).toLocaleString('tr-TR')}
          </text>
          <text x={center} y={center + 26} textAnchor="middle" fontSize="10" fill="#666">
            %{(slices[hov].pct * 100).toFixed(1)}
          </text>
        </>
      ) : null}
    </svg>
  );
}

function Legend({ data = [], maxItems = 10 }) {
  return (
    <div style={{ fontSize: 12, lineHeight: '22px' }}>
      {data.slice(0, maxItems).map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 12, height: 12, borderRadius: 2, background: PALETTE[i % PALETTE.length], flexShrink: 0, display: 'inline-block' }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
          <span style={{ fontWeight: 600, color: '#333' }}>{Number(d.value).toLocaleString('tr-TR')}</span>
        </div>
      ))}
    </div>
  );
}

function BarChart({ data = [], height = 180, color = '#3949ab', valueKey = 'value', labelKey = 'label' }) {
  const max = Math.max(...data.map(d => Number(d[valueKey])), 1);
  const barW = Math.max(20, Math.min(55, 560 / Math.max(data.length, 1) - 6));
  const svgW = data.length * (barW + 6) + 50;
  return (
    <svg width={svgW} height={height + 55} style={{ overflow: 'visible', maxWidth: '100%' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(p => (
        <g key={p}>
          <line x1={36} x2={svgW} y1={10 + (1 - p) * height} y2={10 + (1 - p) * height} stroke="#e0e0e0" />
          <text x={34} y={14 + (1 - p) * height} textAnchor="end" fontSize="9" fill="#999">
            {Math.round(max * p).toLocaleString('tr-TR')}
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const v = Number(d[valueKey]);
        const bh = (v / max) * height;
        const x = 40 + i * (barW + 6);
        const y = 10 + height - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={color} rx={3} opacity={0.85}>
              <title>{d[labelKey]}: {v.toLocaleString('tr-TR')}</title>
            </rect>
            {v > 0 && (
              <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize="9" fill="#333" fontWeight="600">
                {v.toLocaleString('tr-TR')}
              </text>
            )}
            <text x={x + barW / 2} y={height + 24} textAnchor="middle" fontSize="9" fill="#555"
              transform={`rotate(-30 ${x + barW / 2} ${height + 24})`}>
              {String(d[labelKey]).length > 8 ? String(d[labelKey]).slice(0, 8) + '…' : d[labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ datasets = [], height = 180 }) {
  const AY = ['Oca', 'Sub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Agu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const allVals = datasets.flatMap(ds => ds.data);
  const max = Math.max(...allVals, 1);
  const w = 560, pad = 40;
  const xStep = (w - pad * 2) / 11;
  const pt = (i, v) => `${pad + i * xStep},${10 + (1 - v / max) * height}`;
  return (
    <svg width={w} height={height + 50} style={{ overflow: 'visible', maxWidth: '100%' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(p => (
        <g key={p}>
          <line x1={pad} x2={w - pad} y1={10 + (1 - p) * height} y2={10 + (1 - p) * height} stroke="#e0e0e0" />
          <text x={pad - 4} y={14 + (1 - p) * height} textAnchor="end" fontSize="9" fill="#999">
            {Math.round(max * p).toLocaleString('tr-TR')}
          </text>
        </g>
      ))}
      {AY.map((m, i) => (
        <text key={i} x={pad + i * xStep} y={height + 28} textAnchor="middle" fontSize="9" fill="#666">{m}</text>
      ))}
      {datasets.map((ds, di) => {
        const points = ds.data.map((v, i) => pt(i, v)).join(' ');
        return (
          <g key={di}>
            <polyline points={points} fill="none" stroke={PALETTE[di]} strokeWidth="2.5" strokeLinejoin="round" />
            {ds.data.map((v, i) => (
              <circle key={i} cx={pad + i * xStep} cy={10 + (1 - v / max) * height}
                r={3} fill={PALETTE[di]} stroke="#fff" strokeWidth="1.5">
                <title>{AY[i]}: {v.toLocaleString('tr-TR')}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function HBar({ data = [], valueKey = 'value', labelKey = 'label' }) {
  const max = Math.max(...data.map(d => Number(d[valueKey])), 1);
  return (
    <div style={{ fontSize: 12 }}>
      {data.map((d, i) => {
        const v = Number(d[valueKey]);
        const pct = (v / max) * 100;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 130, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#444', flexShrink: 0 }}>
              {d[labelKey]}
            </div>
            <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 18, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length], height: '100%', borderRadius: 4, minWidth: v ? 2 : 0 }} />
            </div>
            <div style={{ width: 52, textAlign: 'right', fontWeight: 600, color: '#333', flexShrink: 0 }}>
              {v.toLocaleString('tr-TR')}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CompareBar({ thisYear = [], prevYear = [], yil, oncekiYil }) {
  const AY = ['Oca','Sub','Mar','Nis','May','Haz','Tem','Agu','Eyl','Eki','Kas','Ara'];
  const map1 = {}, map2 = {};
  thisYear.forEach(r => { map1[r.ay] = Number(r.sayi); });
  prevYear.forEach(r => { map2[r.ay] = Number(r.sayi); });
  const max = Math.max(...AY.map((_, i) => Math.max(map1[i+1]||0, map2[i+1]||0)), 1);
  const svgW = 600, h = 160, padL = 36;
  const bW = (svgW - padL - 20) / 12 / 2 - 2;
  return (
    <svg width={svgW} height={h + 55} style={{ overflow: 'visible', maxWidth: '100%' }}>
      {[0, 0.5, 1].map(p => (
        <g key={p}>
          <line x1={padL} x2={svgW} y1={10 + (1-p)*h} y2={10 + (1-p)*h} stroke="#e8e8e8" />
          <text x={padL - 3} y={14 + (1-p)*h} textAnchor="end" fontSize="9" fill="#999">
            {Math.round(max * p)}
          </text>
        </g>
      ))}
      {AY.map((m, i) => {
        const v1 = map1[i+1]||0, v2 = map2[i+1]||0;
        const x = padL + i * ((svgW - padL) / 12);
        const bh1 = (v1/max)*h, bh2 = (v2/max)*h;
        return (
          <g key={i}>
            <rect x={x+1} y={10+h-bh1} width={bW} height={bh1} fill="#3949ab" rx={2}>
              <title>{yil} {m}: {v1}</title></rect>
            <rect x={x+bW+3} y={10+h-bh2} width={bW} height={bh2} fill="#ef9a9a" rx={2}>
              <title>{oncekiYil} {m}: {v2}</title></rect>
            <text x={x+bW+2} y={h+28} textAnchor="middle" fontSize="9" fill="#666">{m}</text>
          </g>
        );
      })}
      <g transform={`translate(${padL+10}, ${h+42})`}>
        <rect width={10} height={8} fill="#3949ab" rx={1} />
        <text x={13} y={8} fontSize={10} fill="#333">{yil}</text>
        <rect x={60} width={10} height={8} fill="#ef9a9a" rx={1} />
        <text x={73} y={8} fontSize={10} fill="#333">{oncekiYil}</text>
      </g>
    </svg>
  );
}

/* ─────────────────────── KPI CARD ─────────────────────── */
function KpiCard({ label, value, sub, color = '#1a237e', icon = '📊' }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,.08)', borderLeft: `4px solid ${color}`, minWidth: 155 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.2, marginTop: 6 }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,.08)', ...style }}>
      <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#1a237e' }}>{title}</h4>
      {children}
    </div>
  );
}

/* ─────────────────────── HELPERS ─────────────────────── */
const AY_ADI = ['Oca','Sub','Mar','Nis','May','Haz','Tem','Agu','Eyl','Eki','Kas','Ara'];

function aylikDizi(rows, key = 'sayi') {
  const arr = Array(12).fill(0);
  rows.forEach(r => { if (r.ay >= 1 && r.ay <= 12) arr[r.ay - 1] = Number(r[key] || 0); });
  return arr;
}

function fmt(n) { return n != null ? Number(n).toLocaleString('tr-TR') : '—'; }
function fmtTL(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 });
}

/* ─────────────────────── MAIN ─────────────────────── */
export default function Raporlar() {
  const yilSec = new Date().getFullYear();
  const [yil, setYil] = useState(yilSec);
  const [data, setData] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [aktifTab, setAktifTab] = useState('genel');

  const yukle = useCallback(async () => {
    setYukleniyor(true); setHata(null);
    try {
      const r = await api.get(`/raporlar/faaliyet?yil=${yil}`);
      setData(r.data);
    } catch (e) {
      setHata(e.response?.data?.hata || e.message);
    } finally { setYukleniyor(false); }
  }, [yil]);

  useEffect(() => { yukle(); }, [yukle]);

  const TABS = [
    { k: 'genel',         v: 'Genel Bakis' },
    { k: 'ruhsat',        v: 'Ruhsat Analizi' },
    { k: 'harc',          v: 'Harc & Gelir' },
    { k: 'denetim',       v: 'Denetim' },
    { k: 'sikayet',       v: 'Sikayet & Yaptirim' },
    { k: 'karsilastirma', v: 'Karsilastirma' },
  ];

  const exportPDF = () => {
    if (!data) return;
    const doc = createPDF();
    doc.setFont('Arial', 'bold');
    doc.setFontSize(16);
    doc.text(`Ruhsat Faaliyet Raporu - ${yil}`, 105, 18, { align: 'center' });
    doc.setFont('Arial', 'normal');
    doc.setFontSize(10);
    doc.text(`Olusturulma: ${new Date().toLocaleString('tr-TR')}`, 14, 26);

    let y = 34;

    const section = (title) => {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('Arial', 'bold');
      doc.setFontSize(12);
      doc.setFillColor(26, 35, 126);
      doc.rect(14, y - 4, 182, 8, 'F');
      doc.setTextColor(255);
      doc.text(title, 16, y + 0.5);
      doc.setTextColor(0);
      doc.setFont('Arial', 'normal');
      doc.setFontSize(10);
      y += 10;
    };

    const kpi = data.kpi || {};
    section('Genel Ozet');
    autoTable(doc, { ...autoTableDefaults, startY: y,
      head: [['Gosterge', 'Deger']],
      body: [
        ['Toplam Ruhsat', fmt(kpi.toplam_ruhsat)],
        ['Aktif Ruhsat', fmt(kpi.aktif_ruhsat)],
        ['Bekleyen Basvuru', fmt(kpi.bekleyen)],
        ['Askida', fmt(kpi.askida)],
        ['Iptal', fmt(kpi.iptal)],
        ['Vadesi Gecmis', fmt(kpi.vadesi_gecmis)],
        ['Alkol Ruhsatli', fmt(kpi.alkol_ruhsatli)],
        ['Gecici Ruhsat', fmt(kpi.gecici)],
      ],
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    const hk = data.harc_kpi || {};
    section('Harc & Gelir');
    autoTable(doc, { ...autoTableDefaults, startY: y,
      head: [['Gosterge', 'Deger']],
      body: [
        ['Toplam Tahakkuk', fmtTL(hk.toplam_tahakkuk)],
        ['Toplam Tahsilat', fmtTL(hk.toplam_tahsilat)],
        ['Islem Sayisi', fmt(hk.islem_sayisi)],
        ['Odenmemis', fmt(hk.odenmemis_sayi)],
      ],
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    section('Mahalle Yogunlugu (Ilk 10)');
    autoTable(doc, { ...autoTableDefaults, startY: y,
      head: [['Mahalle', 'Ruhsat Sayisi']],
      body: (data.mahalle || []).map(r => [r.mahalle, fmt(r.sayi)]),
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    section('Faaliyet Sinifi (Ilk 10)');
    autoTable(doc, { ...autoTableDefaults, startY: y,
      head: [['Sinif', 'Adet']],
      body: (data.sinif || []).map(r => [r.sinif_adi, fmt(r.sayi)]),
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    const sk = data.sikayet_kpi || {};
    section('Sikayet Ozeti');
    autoTable(doc, { ...autoTableDefaults, startY: y,
      head: [['Gosterge', 'Deger']],
      body: [
        ['Toplam Sikayet', fmt(sk.toplam)],
        ['Acik Sikayet', fmt(sk.acik)],
        ['Cozulen', fmt(sk.cozulen)],
        ['Ort. Cozum Suresi (gun)', fmt(sk.ort_cozum_gun)],
        ['Anonim', fmt(sk.anonim_sayi)],
      ],
    });

    doc.save(`rapor_${yil}.pdf`);
  };

  if (yukleniyor) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Raporlar yukleniyor...</div>;
  if (hata) return <div style={{ padding: 40, color: '#c62828' }}>Hata: {hata}</div>;
  if (!data) return null;

  const { kpi = {}, harc_kpi = {}, sikayet_kpi = {}, islem_suresi = {}, onceki_yil, yil: veriYili } = data;

  const durumDonut = (data.durum || []).map(r => ({ label: r.durum, value: r.sayi }));
  const sinifDonut = (data.sinif || []).map(r => ({ label: r.sinif_adi, value: r.sayi }));
  const turDonut = (data.ruhsat_turu || []).map(r => ({ label: r.tur_adi, value: r.sayi }));
  const gayrisihhiDonut = (data.gayrisihhi || []).map(r => ({ label: r.sinif, value: r.sayi }));
  const odemeDonut = (data.odeme_yontemi || []).map(r => ({ label: r.yontem, value: r.adet }));
  const sikayetKaynakDonut = (data.sikayet_kaynak || []).map(r => ({ label: r.kaynak, value: r.sayi }));
  const yaptirimDonut = (data.yaptirim || []).map(r => ({ label: r.tur, value: r.sayi }));

  const aylikBuYil = aylikDizi(data.aylik || []);
  const oncekiYilArr = aylikDizi(data.onceki_yil_aylik || []);
  const sikayetAylik = aylikDizi(data.sikayet_aylik || []);
  const harcTahakkuk = aylikDizi(data.harc_aylik || [], 'tahakkuk');
  const harcTahsilat = aylikDizi(data.harc_aylik || [], 'tahsilat');

  const mahalleBar = (data.mahalle || []).map(r => ({ label: r.mahalle, value: r.sayi }));
  const sinifBar = (data.sinif || []).map(r => ({ label: r.sinif_adi, value: r.sayi }));
  const harcBar = (data.harc || []).map(r => ({ label: r.harc_turu, value: Math.round(r.odenen_tutar || 0) }));
  const iptalBar = (data.iptal_neden || []).map(r => ({ label: r.neden, value: r.sayi }));

  const denetimTur = {};
  (data.denetim || []).forEach(r => {
    const k = r.denetim_turu || 'Belirtilmemis';
    denetimTur[k] = (denetimTur[k] || 0) + Number(r.sayi);
  });
  const denetimDonut = Object.entries(denetimTur).map(([k, v]) => ({ label: k, value: v }));

  const tahsilatOrani = harc_kpi.toplam_tahakkuk > 0
    ? ((harc_kpi.toplam_tahsilat / harc_kpi.toplam_tahakkuk) * 100).toFixed(1)
    : '—';

  const tabStyle = (k) => ({
    padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
    fontWeight: aktifTab === k ? 700 : 400,
    color: aktifTab === k ? '#1a237e' : '#666',
    borderBottom: aktifTab === k ? '3px solid #1a237e' : '3px solid transparent',
    marginBottom: -2,
  });

  const kpiRow = (items) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(155px,1fr))', gap: 14 }}>
      {items.map((p, i) => <KpiCard key={i} {...p} />)}
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a237e' }}>Faaliyet Raporlari</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={yil} onChange={e => setYil(Number(e.target.value))}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1.5px solid #ccc', fontSize: 14 }}>
            {Array.from({ length: 6 }, (_, i) => yilSec - i).map(y2 => (
              <option key={y2} value={y2}>{y2}</option>
            ))}
          </select>
          <button onClick={exportPDF}
            style={{ padding: '7px 18px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            PDF Indir
          </button>
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: '2px solid #e0e0e0', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.k} onClick={() => setAktifTab(t.k)} style={tabStyle(t.k)}>{t.v}</button>
        ))}
      </div>

      {/* ══ GENEL ══ */}
      {aktifTab === 'genel' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {kpiRow([
            { label: 'Toplam Ruhsat', value: fmt(kpi.toplam_ruhsat), icon: '📋', color: '#1a237e' },
            { label: 'Aktif Ruhsat', value: fmt(kpi.aktif_ruhsat), icon: '✅', color: '#2e7d32' },
            { label: 'Bekleyen Basvuru', value: fmt(kpi.bekleyen), icon: '⏳', color: '#e65100' },
            { label: 'Askida', value: fmt(kpi.askida), icon: '⏸', color: '#6a1b9a' },
            { label: 'Vadesi Gecmis', value: fmt(kpi.vadesi_gecmis), icon: '⚠', color: '#b71c1c' },
            { label: 'Alkol Ruhsatli', value: fmt(kpi.alkol_ruhsatli), icon: '🍺', color: '#37474f' },
            { label: 'Gecici Ruhsat', value: fmt(kpi.gecici), icon: '🕐', color: '#00695c' },
            { label: 'Tahsilat Orani', value: `%${tahsilatOrani}`, icon: '💰', color: '#1565c0', sub: `${fmtTL(harc_kpi.toplam_tahsilat)} tahsil edildi` },
          ])}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
            <ChartCard title={`${veriYili} Yili Aylik Basvuru Trendi`}>
              <div style={{ overflowX: 'auto' }}>
                <LineChart datasets={[{ data: aylikBuYil }]} height={160} />
              </div>
            </ChartCard>
            <ChartCard title="Ruhsat Durum Dagilimi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={durumDonut} size={160} />
                <Legend data={durumDonut} />
              </div>
            </ChartCard>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ChartCard title="Mahalle Bazli Yogunluk (Ilk 10)">
              <HBar data={mahalleBar} />
            </ChartCard>
            <ChartCard title="Faaliyet Sinifi (Ilk 10)">
              <HBar data={sinifBar} />
            </ChartCard>
          </div>
        </div>
      )}

      {/* ══ RUHSAT ANALİZİ ══ */}
      {aktifTab === 'ruhsat' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {kpiRow([
            { label: 'Ort. Onay Suresi', value: islem_suresi.ort_gun ? `${islem_suresi.ort_gun} gun` : '—', icon: '⏱', color: '#1565c0' },
            { label: 'Min. Onay Suresi', value: islem_suresi.min_gun ? `${islem_suresi.min_gun} gun` : '—', icon: '🏃', color: '#2e7d32' },
            { label: 'Max. Onay Suresi', value: islem_suresi.max_gun ? `${islem_suresi.max_gun} gun` : '—', icon: '🐌', color: '#b71c1c' },
            { label: `${veriYili} Onaylanan`, value: fmt(islem_suresi.onaylanan_sayi), icon: '✅', color: '#1a237e' },
          ])}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ChartCard title="Ruhsat Turu Dagilimi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={turDonut} size={180} />
                <Legend data={turDonut} />
              </div>
            </ChartCard>
            <ChartCard title="Gayrisihhi Sinif Dagilimi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={gayrisihhiDonut} size={180} />
                <Legend data={gayrisihhiDonut} />
              </div>
            </ChartCard>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ChartCard title="Mahalle Yogunlugu (Ilk 10)">
              <HBar data={mahalleBar} />
            </ChartCard>
            <ChartCard title="Iptal Nedenleri">
              {iptalBar.length > 0
                ? <HBar data={iptalBar} />
                : <div style={{ color: '#999', fontSize: 13 }}>Bu yil iptal kaydi yok.</div>}
            </ChartCard>
          </div>

          <ChartCard title="Aylik Basvuru Dagilimi">
            <div style={{ overflowX: 'auto' }}>
              <BarChart data={AY_ADI.map((m, i) => ({ label: m, value: aylikBuYil[i] }))} height={160} />
            </div>
          </ChartCard>
        </div>
      )}

      {/* ══ HARÇ ══ */}
      {aktifTab === 'harc' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {kpiRow([
            { label: 'Toplam Tahakkuk', value: fmtTL(harc_kpi.toplam_tahakkuk), icon: '📄', color: '#1a237e' },
            { label: 'Toplam Tahsilat', value: fmtTL(harc_kpi.toplam_tahsilat), icon: '💰', color: '#2e7d32' },
            { label: 'Tahsilat Orani', value: `%${tahsilatOrani}`, icon: '📊', color: '#e65100' },
            { label: 'Odenmemis Islem', value: fmt(harc_kpi.odenmemis_sayi), icon: '❌', color: '#b71c1c' },
          ])}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
            <ChartCard title="Aylik Tahakkuk vs Tahsilat">
              <div style={{ overflowX: 'auto' }}>
                <LineChart datasets={[{ data: harcTahakkuk }, { data: harcTahsilat }]} height={160} />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                <span><span style={{ color: PALETTE[0] }}>■</span> Tahakkuk</span>
                <span><span style={{ color: PALETTE[1] }}>■</span> Tahsilat</span>
              </div>
            </ChartCard>
            <ChartCard title="Odeme Yontemi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={odemeDonut} size={150} />
                <Legend data={odemeDonut} />
              </div>
            </ChartCard>
          </div>

          <ChartCard title="Harc Turune Gore Tahsilat">
            {harcBar.length > 0
              ? <HBar data={harcBar} />
              : <div style={{ color: '#999', fontSize: 13 }}>Bu yil harc kaydi yok.</div>}
          </ChartCard>

          {(data.harc || []).length > 0 && (
            <ChartCard title="Harc Turu Detay Tablosu">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#1a237e', color: '#fff' }}>
                      {['Harc Turu','Adet','Toplam Tahakkuk','Tahsil Edilen','Oran'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Harc Turu' ? 'left' : 'right' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.harc.map((r, i) => {
                      const oran = r.toplam_tutar > 0 ? ((r.odenen_tutar / r.toplam_tutar) * 100).toFixed(1) : '0.0';
                      return (
                        <tr key={i} style={{ background: i % 2 ? '#f5f7ff' : '#fff' }}>
                          <td style={{ padding: '7px 12px' }}>{r.harc_turu}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right' }}>{fmt(r.adet)}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right' }}>{fmtTL(r.toplam_tutar)}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right' }}>{fmtTL(r.odenen_tutar)}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: Number(oran) >= 80 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                            %{oran}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}
        </div>
      )}

      {/* ══ DENETİM ══ */}
      {aktifTab === 'denetim' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {(() => {
            const toplamDenetim = (data.denetim || []).reduce((s, r) => s + Number(r.sayi), 0);
            const yaptirimGereken = (data.denetim || []).reduce((s, r) => s + Number(r.yaptirim_sayi || 0), 0);
            const tamamlanan = (data.denetim || []).filter(r => r.durum === 'TAMAMLANDI').reduce((s, r) => s + Number(r.sayi), 0);
            const toplamYaptirim = (data.yaptirim || []).reduce((s, r) => s + Number(r.sayi), 0);
            return kpiRow([
              { label: 'Toplam Denetim', value: fmt(toplamDenetim), icon: '🔍', color: '#1a237e' },
              { label: 'Tamamlanan', value: fmt(tamamlanan), icon: '✅', color: '#2e7d32' },
              { label: 'Yaptirim Gereken', value: fmt(yaptirimGereken), icon: '⚠', color: '#b71c1c' },
              { label: 'Toplam Yaptirim', value: fmt(toplamYaptirim), icon: '🚫', color: '#e65100' },
            ]);
          })()}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ChartCard title="Denetim Turu Dagilimi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={denetimDonut} size={180} />
                <Legend data={denetimDonut} />
              </div>
            </ChartCard>
            <ChartCard title="Yaptirim Turu Dagilimi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={yaptirimDonut} size={180} />
                <Legend data={yaptirimDonut} />
              </div>
            </ChartCard>
          </div>

          {(data.yaptirim || []).length > 0 && (
            <ChartCard title="Yaptirim & Ceza Tutarlari">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#1a237e', color: '#fff' }}>
                      {['Tur','Adet','Aktif','Toplam Ceza','Tahsil Edilen'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Tur' ? 'left' : 'right' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.yaptirim.map((r, i) => (
                      <tr key={i} style={{ background: i % 2 ? '#f5f7ff' : '#fff' }}>
                        <td style={{ padding: '7px 12px' }}>{r.tur}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{fmt(r.sayi)}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', color: r.aktif_sayi > 0 ? '#c62828' : '#2e7d32', fontWeight: 700 }}>{fmt(r.aktif_sayi)}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{fmtTL(r.toplam_ceza)}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{fmtTL(r.odenen_ceza)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}
        </div>
      )}

      {/* ══ ŞİKAYET ══ */}
      {aktifTab === 'sikayet' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {kpiRow([
            { label: 'Toplam Sikayet', value: fmt(sikayet_kpi.toplam), icon: '📣', color: '#1a237e' },
            { label: 'Acik Sikayet', value: fmt(sikayet_kpi.acik), icon: '🔴', color: '#b71c1c' },
            { label: 'Cozulen', value: fmt(sikayet_kpi.cozulen), icon: '✅', color: '#2e7d32' },
            { label: 'Ort. Cozum Suresi', value: sikayet_kpi.ort_cozum_gun ? `${sikayet_kpi.ort_cozum_gun} gun` : '—', icon: '⏱', color: '#e65100' },
            { label: 'Anonim Sikayet', value: fmt(sikayet_kpi.anonim_sayi), icon: '🎭', color: '#6a1b9a' },
          ])}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
            <ChartCard title="Aylik Sikayet Trendi">
              <div style={{ overflowX: 'auto' }}>
                <LineChart datasets={[{ data: sikayetAylik }]} height={160} />
              </div>
            </ChartCard>
            <ChartCard title="Sikayet Kaynagi">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <DonutChart data={sikayetKaynakDonut} size={150} />
                <Legend data={sikayetKaynakDonut} />
              </div>
            </ChartCard>
          </div>

          {(data.sikayet || []).length > 0 && (
            <ChartCard title="Sikayet Konusu (Ilk 10)">
              <HBar data={(data.sikayet || []).map(r => ({ label: r.sikayet_konusu, value: r.sayi }))} />
            </ChartCard>
          )}
        </div>
      )}

      {/* ══ KARŞILAŞTIRMA ══ */}
      {aktifTab === 'karsilastirma' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <ChartCard title={`Aylik Basvuru Karsilastirmasi: ${veriYili} vs ${onceki_yil}`}>
            <div style={{ overflowX: 'auto' }}>
              <CompareBar thisYear={data.aylik || []} prevYear={data.onceki_yil_aylik || []} yil={veriYili} oncekiYil={onceki_yil} />
            </div>
          </ChartCard>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ChartCard title={`${veriYili} Aylik Dagilim`}>
              <BarChart data={AY_ADI.map((m, i) => ({ label: m, value: aylikBuYil[i] }))} height={150} color="#3949ab" />
            </ChartCard>
            <ChartCard title={`${onceki_yil} Aylik Dagilim`}>
              <BarChart data={AY_ADI.map((m, i) => ({ label: m, value: oncekiYilArr[i] }))} height={150} color="#ef9a9a" />
            </ChartCard>
          </div>

          <ChartCard title="Karsilastirma Tablosu">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1a237e', color: '#fff' }}>
                    {['Ay', String(veriYili), String(onceki_yil), 'Fark', 'Degisim'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Ay' ? 'left' : 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {AY_ADI.map((m, i) => {
                    const a = aylikBuYil[i], b = oncekiYilArr[i];
                    const fark = a - b;
                    const pct = b > 0 ? ((fark / b) * 100).toFixed(1) : null;
                    return (
                      <tr key={i} style={{ background: i % 2 ? '#f5f7ff' : '#fff' }}>
                        <td style={{ padding: '7px 12px', fontWeight: 600 }}>{m}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{a}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{b}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', color: fark >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                          {fark >= 0 ? '+' : ''}{fark}
                        </td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', color: fark >= 0 ? '#2e7d32' : '#c62828' }}>
                          {pct != null ? `%${pct}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {(() => {
                    const sumA = aylikBuYil.reduce((s, v) => s + v, 0);
                    const sumB = oncekiYilArr.reduce((s, v) => s + v, 0);
                    const fark = sumA - sumB;
                    const pct = sumB > 0 ? ((fark / sumB) * 100).toFixed(1) : null;
                    return (
                      <tr style={{ background: '#e8eaf6', fontWeight: 700 }}>
                        <td style={{ padding: '7px 12px' }}>TOPLAM</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{sumA}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{sumB}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', color: fark >= 0 ? '#2e7d32' : '#c62828' }}>
                          {fark >= 0 ? '+' : ''}{fark}
                        </td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', color: fark >= 0 ? '#2e7d32' : '#c62828' }}>
                          {pct != null ? `%${pct}` : '—'}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
